import type { PersistedClient, Persister } from '@tanstack/query-persist-client-core'

type SqliteDB = Awaited<ReturnType<typeof import('@tauri-apps/plugin-sql').default.load>>

let dbInstance: SqliteDB | null = null

async function getDb(): Promise<SqliteDB> {
  if (!dbInstance) {
    const mod = await import('@tauri-apps/plugin-sql')
    dbInstance = await mod.default.load('sqlite:cache.db')

    await dbInstance.execute(`
      CREATE TABLE IF NOT EXISTS query_cache (
        key TEXT PRIMARY KEY,
        value TEXT NOT NULL,
        updated_at INTEGER NOT NULL
      )
    `)
  }
  return dbInstance
}

/**
 * Remove all cache entries except for the given active key.
 * Cleans up stale entries from old versions / other users.
 */
async function cleanupStaleEntries(activeKey: string): Promise<void> {
  try {
    const db = await getDb()
    await db.execute(`DELETE FROM query_cache WHERE key != $1`, [activeKey])
  } catch (e) {
    console.warn('[QueryCache:SQLite] Failed to clean stale entries', e)
  }
}

export async function createSQLitePersister(cacheKey: string): Promise<Persister> {
  const db = await getDb()

  // Opportunistically clean up old version/user cache entries
  void cleanupStaleEntries(cacheKey)

  return {
    persistClient: async (client: PersistedClient) => {
      try {
        const value = JSON.stringify(client)
        await db.execute(
          `INSERT OR REPLACE INTO query_cache (key, value, updated_at) VALUES ($1, $2, $3)`,
          [cacheKey, value, Date.now()],
        )
      } catch (e) {
        console.warn('[QueryCache:SQLite] Failed to persist client', e)
      }
    },
    restoreClient: async () => {
      try {
        const rows = await db.select<{ value: string }[]>(
          `SELECT value FROM query_cache WHERE key = $1`,
          [cacheKey],
        )
        if (rows.length === 0) return undefined
        return JSON.parse(rows[0].value) as PersistedClient
      } catch (e) {
        console.warn('[QueryCache:SQLite] Failed to restore client', e)
        return undefined
      }
    },
    removeClient: async () => {
      try {
        await db.execute(`DELETE FROM query_cache WHERE key = $1`, [cacheKey])
      } catch (e) {
        console.warn('[QueryCache:SQLite] Failed to remove client', e)
      }
    },
  }
}
